import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {downloadChartPng, downloadCsv} from './timespanUtils';

const OTHER = '__other__';

const SERIES = [
  {key: 'self_service', label: 'Self-Service', color: '#2196F3'},
  {key: 'sfdc', label: 'SFDC', color: '#4CAF50'},
  {key: 'manual', label: 'Manual', color: '#FF9800'},
];

// Derive sorted, de-duped family list from the chart data rows.
// product_family does not exist on variant template objects — use d.family
// from the API response data which is already populated.
const useFamilies = data => {
  return useMemo(() => {
    if (!data?.length) return [];
    const seen = new Set();
    const named = data
      .map(d => d.family)
      .filter(f => f && !/test/i.test(f) && !seen.has(f) && seen.add(f))
      .sort();
    const hasOther = data.some(d => !d.family);
    return hasOther ? [...named, OTHER] : named;
  }, [data]);
};

const LABEL_MAX_CHARS = 28;
const truncateLabel = name =>
  name.length > LABEL_MAX_CHARS ? `${name.slice(0, LABEL_MAX_CHARS)}…` : name;

const STATUS_OPTIONS = [
  {value: null, label: 'All'},
  {value: 'active', label: 'Active'},
  {value: 'pending', label: 'Pending'},
  {value: 'expired', label: 'Expired'},
];

const ProvisionsByProductChart = ({
  data,
  isLoading,
  error,
  timespan,
  status,
  onStatusChange,
}) => {
  const cardRef = useRef(null);
  const families = useFamilies(data);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [stacked, setStacked] = useState(true);

  useEffect(() => {
    setSelectedFamily(null);
  }, [timespan, status]);

  // Full sorted+filtered set — used for CSV export and totals footer.
  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data;
    if (selectedFamily) {
      rows =
        selectedFamily === OTHER
          ? data.filter(d => !d.family)
          : data.filter(d => d.family === selectedFamily);
    }
    return [...rows].sort((a, b) => {
      const aTotal = (a.self_service ?? 0) + (a.sfdc ?? 0) + (a.manual ?? 0);
      const bTotal = (b.self_service ?? 0) + (b.sfdc ?? 0) + (b.manual ?? 0);
      return bTotal - aTotal;
    });
  }, [data, selectedFamily]);

  // Top 20 by total descending, with _total pre-computed for LabelList.
  // ADR 0003 §6 caps the chart at 20 products; CSV export uses filtered (full set).
  const chartData = useMemo(
    () =>
      filtered.slice(0, 20).map(d => ({
        ...d,
        _total: (d.self_service ?? 0) + (d.sfdc ?? 0) + (d.manual ?? 0),
      })),
    [filtered]
  );

  const handleExport = () => {
    if (!filtered.length) return;
    const rows = filtered.map(d => ({
      Product: d.product_name,
      Family: d.family ?? 'Other',
      'Self-Service': d.self_service ?? 0,
      SFDC: d.sfdc ?? 0,
      Manual: d.manual ?? 0,
      Total: (d.self_service ?? 0) + (d.sfdc ?? 0) + (d.manual ?? 0),
    }));
    downloadCsv(rows, `provisions-by-product-${timespan}.csv`);
  };

  const handleDownloadPng = () =>
    downloadChartPng(cardRef.current, `provisions-by-product-${timespan}.png`);

  // Bar chart: horizontal layout (product names on Y-axis — more readable)
  // Legend is omitted here; the colored totals footer below the divider serves as legend.
  const renderBarChart = () => (
    <ResponsiveContainer
      width="100%"
      height={Math.max(240, chartData.length * 36)}
    >
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{top: 4, right: 48, left: 8, bottom: 4}}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          allowDecimals={false}
          tick={{fontSize: 12}}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="product_name"
          tickFormatter={truncateLabel}
          tick={{fontSize: 12}}
          tickLine={false}
          width={210}
        />
        <Tooltip
          formatter={(value, name) => [value, name]}
          labelFormatter={name => name}
        />
        {SERIES.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={s.color}
            stackId={stacked ? 'stack' : undefined}
          >
            {stacked && i === SERIES.length - 1 && (
              <LabelList
                dataKey="_total"
                position="right"
                style={{fontSize: 11, fill: '#666'}}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <Card ref={cardRef} variant="outlined" sx={{mb: 3}}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6">Provisions by Product</Typography>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!filtered.length}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              startIcon={<CameraAltOutlinedIcon />}
              onClick={handleDownloadPng}
              disabled={!filtered.length}
            >
              PNG
            </Button>
          </Stack>
        </Stack>

        {/* Family chips — sourced from live product catalog */}
        {families.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} mb={2}>
            {families.map(family => (
              <Chip
                key={family}
                label={family === OTHER ? 'Other' : family}
                size="small"
                color={selectedFamily === family ? 'primary' : 'default'}
                variant={selectedFamily === family ? 'filled' : 'outlined'}
                onClick={() =>
                  setSelectedFamily(prev => (prev === family ? null : family))
                }
              />
            ))}
          </Stack>
        )}

        {/* Status filter + stack toggle */}
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          mb={2}
          flexWrap="wrap"
        >
          <ToggleButtonGroup
            value={status ?? null}
            exclusive
            onChange={(_, v) => onStatusChange(v)}
            size="small"
          >
            {STATUS_OPTIONS.map(o => (
              <ToggleButton key={String(o.value)} value={o.value}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <FormControlLabel
            control={
              <Switch
                checked={stacked}
                onChange={e => setStacked(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Stacked
              </Typography>
            }
          />

          {selectedFamily && (
            <Typography variant="caption" color="text.secondary">
              Showing {filtered.length} product
              {filtered.length !== 1 ? 's' : ''} in{' '}
              <strong>
                {selectedFamily === OTHER ? 'Other' : selectedFamily}
              </strong>
            </Typography>
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{mb: 2}}>
            Failed to load product data. Please try refreshing.
          </Alert>
        )}

        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!isLoading && !error && filtered.length === 0 && data && (
          <Typography
            variant="body2"
            color="text.secondary"
            py={4}
            textAlign="center"
          >
            No provisioning data for this period.
          </Typography>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <>
            {renderBarChart()}
            <Divider sx={{my: 2}} />
            <Stack direction="row" gap={4} flexWrap="wrap">
              {SERIES.map(s => (
                <Box key={s.key}>
                  <Typography variant="caption" color="text.secondary">
                    {s.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{color: s.color, fontWeight: 700}}
                  >
                    {filtered
                      .reduce((sum, d) => sum + (d[s.key] ?? 0), 0)
                      .toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

ProvisionsByProductChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  timespan: PropTypes.string.isRequired,
  status: PropTypes.string,
  onStatusChange: PropTypes.func.isRequired,
};

export default ProvisionsByProductChart;
